import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import { useTenant } from "@/context/useTenant";
import MainLayout from "@/layouts/MainLayout";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import routes from "@/utils/routes";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const { tenantId } = useTenant();

  // If no tenant ID is available (set VITE_X_TENANTID in .env), show message
  if (!tenantId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Tenant ID Required</h1>
          <p className="text-gray-600">
            Set VITE_X_TENANTID in your .env file to configure the tenant.
          </p>
        </div>
      </div>
    );
  }
  const publicRoutes = routes.find(m => m.type === "public")?.routes ?? [];
  const authRoutes = routes.find(m => m.type === "auth")?.routes ?? [];
  const authRoutesWithoutRoot = authRoutes.filter(route => route.path !== "/");

  // Routes that handle their own nested routing (project apps)
  const projectAppPaths = ["/", "/jainboarding-admin"];
  const jainBoardingRoute = publicRoutes.find(r => r.path === "/");
  const jainboardingAdminRoute = publicRoutes.find(
    r => r.path === "/jainboarding-admin"
  );
  const otherPublicRoutes = publicRoutes.filter(
    r => !projectAppPaths.includes(r.path as string)
  );

  const JainBoardingComponent = jainBoardingRoute?.element;
  const JainboardingAdminComponent = jainboardingAdminRoute?.element;

  return (
    <BrowserRouter basename="/">
      <Routes>
        {/* Project apps with their own routing - match first, no layout wrapper */}
        {jainboardingAdminRoute && JainboardingAdminComponent && (
          <Route
            path="jainboarding-admin/*"
            element={<JainboardingAdminComponent />}
          />
        )}
        {/* Legacy user prefix -> root user home */}
        <Route path="jainboarding/*" element={<Navigate to="/" replace />} />
        {jainBoardingRoute && JainBoardingComponent && (
          <Route path="/*" element={<JainBoardingComponent />} />
        )}

        <Route element={<TopLevelComponent />}>
          {otherPublicRoutes.map(({ path, element: Page }) => (
            <Route key={path} path={path} element={<StarterLayout />}>
              <Route index element={<Page />} />
            </Route>
          ))}

          {/* Protected routes */}
          <Route element={<ProtectedRouter />}>
            <Route element={<MainLayout />}>
              {authRoutesWithoutRoot.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
